import { useState } from "react";
import { ButtonAsButton } from "@made-people/blueprint-button";
import { useTranslate } from "@made-people/grease-translations";
import type { FormEvent } from "react";
import { useMmBackendClient } from "../../../../hooks/use-mm-backend-client";
import type { AxiosInstance } from "axios";

export type NonInretrnWithdrawFormData = {
  orderNumber: string;
  email: string;
};

type WithdrawRequestResult = {
  success: boolean;
  message: string;
};

type WithdrawErrorCode =
  | "INVALID_PAYLOAD"
  | "ORDER_NOT_FOUND"
  | "ORDER_NOT_FOUND_FOR_EMAIL"
  | "WITHDRAW_NOT_AVAILABLE"
  | "WITHDRAW_WINDOW_EXPIRED"
  | "FAILED_TO_SEND_WITHDRAW_ORDER_EMAIL"
  | "INTERNAL_SERVER_ERROR";

const REQUEST_TIMEOUT_MS = 15000;

const getErrorMessageByCode = (
  errorCode: WithdrawErrorCode | undefined,
  translate: any,
): string => {
  if (errorCode === "INVALID_PAYLOAD") {
    return translate("Withdraw_Form_Submit_Error_InvalidOrderOrEmail");
  }

  if (errorCode === "ORDER_NOT_FOUND") {
    return translate("Withdraw_Form_Submit_Error_OrderNotFound");
  }

  if (errorCode === "ORDER_NOT_FOUND_FOR_EMAIL") {
    return translate("Withdraw_Form_Submit_Error_NoOrderFoundOnThisEmail");
  }

  if (errorCode === "WITHDRAW_NOT_AVAILABLE") {
    return translate("Withdraw_Form_Submit_Error_NoShipmentsAvailable");
  }

  if (errorCode === "WITHDRAW_WINDOW_EXPIRED") {
    return translate("Withdraw_Form_Submit_Error_Expired");
  }

  if (errorCode === "FAILED_TO_SEND_WITHDRAW_ORDER_EMAIL") {
    return translate("Withdraw_Form_Submit_Error_GeneralError");
  }

  if (errorCode === "INTERNAL_SERVER_ERROR") {
    return translate("Withdraw_Form_Submit_Error_InternalServerError");
  }

  return translate("Withdraw_Form_Submit_Error_GeneralError");
};

const requestWithdrawOrder = async (
  data: NonInretrnWithdrawFormData,
  signal: AbortSignal,
  translate: any,
  mmBackendClient: AxiosInstance,
): Promise<WithdrawRequestResult> => {
  try {
    const response = await mmBackendClient.post(
      "/withdraw/withdraw-order",
      {
        orderId: data.orderNumber.trim(),
        email: data.email.trim(),
      },
      {
        signal,
      },
    );
    if (response.status >= 200 && response.status < 300) {
      return {
        success: true,
        message: translate("Withdraw_Form_Submit_Success"),
      };
    } else {
      return {
        success: false,
        message: translate("Withdraw_Form_Submit_Error_GeneralError"),
      };
    }
  } catch (error: any) {
    return {
      success: false,
      message: getErrorMessageByCode(
        error?.response?.data?.errorCode,
        translate,
      ),
    };
  }
};

export const NonInretrnWithdrawForm = () => {
  const mmBackendClient = useMmBackendClient();
  const translate = useTranslate();
  const [orderNumber, setOrderNumber] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] =
    useState<WithdrawRequestResult | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      abortController.abort();
    }, REQUEST_TIMEOUT_MS);

    try {
      const result = await requestWithdrawOrder(
        { orderNumber, email },
        abortController.signal,
        translate,
        mmBackendClient,
      );

      setSubmitResult(result);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setSubmitResult({
          success: false,
          message: translate("Withdraw_Form_Submit_Error_TimeOut"),
        });
      } else {
        setSubmitResult({
          success: false,
          message: translate("Withdraw_Form_Submit_Error_GeneralError"),
        });
      }
    } finally {
      clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-component-spacing-3xl mb-component-spacing-5xl px-component-spacing-sm">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md mx-auto flex flex-col gap-4"
      >
        <label className="block rounded-md border border-gray-300 px-4 py-4 transition-colors focus-within:border-gray-700">
          <input
            type="text"
            required
            aria-label="Order number"
            placeholder={translate("Withdraw_Form_OrderNumber_Label")}
            value={orderNumber}
            onChange={(event) => setOrderNumber(event.target.value)}
            className="w-full appearance-none border-0 bg-transparent text-sm text-gray-700 outline-none focus:border-0 focus:outline-none focus:ring-0 placeholder:text-sm placeholder:text-gray-600"
          />
        </label>

        <label className="block rounded-md border border-gray-300 px-4 py-4 transition-colors focus-within:border-gray-700">
          <input
            type="email"
            required
            aria-label="E-mail"
            placeholder={translate("Withdraw_Form_Email_Label")}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="w-full appearance-none border-0 bg-transparent text-sm text-gray-700 outline-none focus:border-0 focus:outline-none focus:ring-0 placeholder:text-sm placeholder:text-gray-600"
          />
        </label>

        <div className="w-full">
          <ButtonAsButton
            label={translate("Withdraw_Form_Submit_Button_Text")}
            type="submit"
            disabled={isSubmitting}
            progress={isSubmitting ? "loading" : "default"}
            variants={{ width: "full", rounded: "md" }}
            className="!bg-[#bdbdbd] hover:!bg-[#adadad] [&>div>span]:!text-gray-700"
          />
        </div>
        {submitResult && (
          <p
            role={submitResult.success ? "status" : "alert"}
            className={`text-sm ${
              submitResult.success ? "text-green-700" : "text-red-700"
            }`}
          >
            {submitResult.message}
          </p>
        )}
      </form>
    </div>
  );
};
